import type { ResolveShopifyApprovalRedirectResponseBodyType } from "@intelligems/ig-types/functions/billing-workflow/shopify-approval/shopify-approval";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import {
  mpBillingShopifyApprovalStarted,
  type MpBillingShopifyApprovalStartedProps,
} from "@src/utils/mixpanel";

import { api } from "../../api";
import type { ApiRequest } from "../../types/api";

type ResolveShopifyApprovalRedirectRequest = ApiRequest<
  undefined,
  { approvalId: string },
  undefined
>;

// Resolving the redirect is the merchant leaving for the approval screen, so
// the caller states what it is resuming and the hook announces it at the one
// moment the url is in hand.
export type ResolveShopifyApprovalRedirectVariables =
  ResolveShopifyApprovalRedirectRequest & {
    analytics: MpBillingShopifyApprovalStartedProps;
  };

type ResolveShopifyApprovalRedirectOptions = Omit<
  UseMutationOptions<
    ResolveShopifyApprovalRedirectResponseBodyType,
    Error,
    ResolveShopifyApprovalRedirectVariables
  >,
  "mutationFn" | "mutationKey"
>;

// The endpoint resolves to JSON `{ url }` rather than a 302 so the request can
// carry the member Authorization header. The hook, not the consumer, opens the
// resolved url — see its onSuccess.
export const resolveShopifyApprovalRedirect = async (
  variables: ResolveShopifyApprovalRedirectRequest
): Promise<ResolveShopifyApprovalRedirectResponseBodyType> => {
  const resp = await api.get<ResolveShopifyApprovalRedirectResponseBodyType>(
    `/v3/billing/shopify-approval/${variables.pathParams.approvalId}/redirect`
  );
  return resp.data;
};

export const useResolveShopifyApprovalRedirect = (
  options?: ResolveShopifyApprovalRedirectOptions
) =>
  useMutation<
    ResolveShopifyApprovalRedirectResponseBodyType,
    Error,
    ResolveShopifyApprovalRedirectVariables
  >({
    ...options,
    mutationKey: ["resolve-shopify-approval-redirect"],
    mutationFn: resolveShopifyApprovalRedirect,
    // Returned, not just called: React Query awaits whatever onSuccess resolves
    // before settling the mutation, and dropping the consumer's promise would
    // strip that guarantee from every caller.
    onSuccess: (data, variables, ...rest) => {
      mpBillingShopifyApprovalStarted(variables.analytics);
      window.open(data.url, "_blank");
      return options?.onSuccess?.(data, variables, ...rest);
    },
  });
